import { useState } from "react";

import { useSocket } from "../../lib/socket";
import type { User } from "../../types";
import { AppFrame } from "../../components/AppFrame";
import { ImportCompaniesDialog } from "../../components/crm/ImportCompaniesDialog";
import { CompanyDialog } from "../../components/crm/CompanyDialog";
import { CrmSidebar } from "../../components/crm/CrmSidebar";
import { Button } from "../../components/ui/Button";

/**
 * The CRM's shell: the same frame the Helpdesk renders in, with the CRM's own
 * navigation in the sidebar and its own toolbar above the screen.
 *
 * The socket is shared with the helpdesk rather than a second connection --
 * there is one server, one session and one Socket.IO namespace, and the
 * connection dot means the same thing on both sides of the rail.
 */
export function CrmShell({ user }: { user: User }) {
  const { connected } = useSocket();
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);

  return (
    <>
      {/*
        Outside the frame so the dialog is never clipped by a pane, and mounted
        here rather than in a page so it survives navigation -- somebody adding
        a company from the duplicate queue stays where they were.
      */}
      {creating ? <CompanyDialog onClose={() => setCreating(false)} /> : null}
      {importing ? (
        <ImportCompaniesDialog onClose={() => setImporting(false)} />
      ) : null}

      <AppFrame
        user={user}
        title="CRM"
        connected={connected}
        sidebar={<CrmSidebar connected={connected} />}
        toolbar={
          /*
           * Adding an account is the one action wanted from every screen in the
           * CRM -- somebody looking at a duplicate, or filing a contact, is
           * exactly as likely to need it as somebody on the list. So it lives
           * with the product rather than on one page.
           */
          <>
            {/*
              Admin-only, and the server enforces it -- creating four hundred
              accounts from a column somebody mapped wrongly is not the same
              act as creating one, and there is no bulk undo. Hidden rather
              than disabled: an agent has no way to become an admin, so a
              greyed button would only be a question they cannot answer.

              Not on a phone either. Mapping a spreadsheet's columns is desk
              work, and on a phone the button only took room in a toolbar that
              is already the name of the page, New company and the avatar.
            */}
            {user.role === "admin" ? (
              <Button
                size="sm"
                variant="secondary"
                className="max-md:hidden"
                onClick={() => setImporting(true)}
              >
                Import
              </Button>
            ) : null}
            <Button size="sm" onClick={() => setCreating(true)}>
              New company
            </Button>
          </>
        }
      />
    </>
  );
}
